import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";

import {
    Grid,
    Typography,
    Button,
    Chip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from "@material-ui/core";
import { getStatusDisplayInfo } from "../../components/superPay/SuperPayStatusPanel";

const HOT_PINK = "#ff0c9c";
const PAGE_LIMIT = 20;

@inject("profileStore", "templateStore", "hgSuperPayStore")
@withRouter
@observer
class HGSuperPayContributionHistory extends Component {
    state = {
        employees: [],
        historyStartDate: "",
        historyEndDate: "",
        historyEmployeeId: "",
        contributionHistory: [],
        pagination: null,
        currentPage: 1,
        historyLoading: false,
        historyError: null,
        historySearched: false
    };

    componentDidMount() {
        this._loadEmployees();
    }

    componentWillUnmount() {
        this._employeesRequestId = (this._employeesRequestId || 0) + 1;
    }

    _loadEmployees = async () => {
        const { profile } = this.props.profileStore;
        const profileId = this.props.profileId || (profile && profile.id);
        if (!profileId) return;
        const requestId = (this._employeesRequestId || 0) + 1;
        this._employeesRequestId = requestId;
        const employees = [];
        const employeeIds = new Set();
        let page = 1;
        let pagination = null;

        try {
            do {
                const result = await this.props.hgSuperPayStore.getEmployeeMapping(profileId, page, 100);
                (result.items || []).forEach(employee => {
                    const employeeId = employee.contactId;
                    if (!employeeIds.has(employeeId)) {
                        employeeIds.add(employeeId);
                        employees.push(employee);
                    }
                });
                pagination = result.pagination;
                page += 1;
            } while (pagination && pagination.hasNext);

            if (requestId === this._employeesRequestId) {
                this.setState({ employees });
            }
        } catch (error) {
            // Contribution history remains usable even if the optional employee filter cannot load.
        }
    };

    loadPage = async (page) => {
        const { historyStartDate, historyEndDate, historyEmployeeId } = this.state;
        const { profile } = this.props.profileStore;
        const profileId = this.props.profileId || (profile && profile.id);
        this.setState({ historyLoading: true, historyError: null });
        try {
            const result = await this.props.hgSuperPayStore.getContributionHistory(
                historyEmployeeId || undefined,
                historyStartDate || undefined,
                historyEndDate || undefined,
                profileId || undefined,
                page,
                PAGE_LIMIT,
                this.props.organisationId || undefined
            );
            this.setState({
                contributionHistory: result.contributions || [],
                pagination: result.pagination || null,
                currentPage: page
            });
        } catch (e) {
            const status = e?.response?.status || e?.status;
            if (status === 404) {
                this.setState({ contributionHistory: [], pagination: null, historyError: null });
            } else {
                this.setState({ historyError: e?.response?.data?.message || e?.message || "Failed to load contribution history" });
            }
        } finally {
            this.setState({ historyLoading: false });
        }
    };

    handleSearchHistory = async () => {
        this.setState({ historySearched: true, contributionHistory: [], pagination: null });
        await this.loadPage(1);
    };

    handlePageChange = (page) => {
        this.loadPage(page);
    };

    renderRow = (row, idx) => {
        const emp = row.employee;
        const displayName = emp
            ? (emp.profileName || `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || "—")
            : (row.employeeName || "—");

        const amountVal = typeof row.amount === "number" ? row.amount : row.amount?.value;
        const currency = row.amount?.currency || "AUD";
        const amountDisplay = amountVal != null ? `${currency} ${Number(amountVal).toFixed(2)}` : "—";

        const fundName = row.contributionDetails?.fund?.name || row.usi || "—";
        const usi = row.contributionDetails?.fund?.usi || row.usi;

        const fmtTs = ts => {
            if (ts == null || ts === "") return null;

            const timestamp = Number(ts);
            const date = Number.isFinite(timestamp)
                ? new Date(Math.abs(timestamp) < 100000000000 ? timestamp * 1000 : timestamp)
                : new Date(ts);
            if (Number.isNaN(date.getTime())) return null;

            return date.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
        };
        const statusHistory = Array.isArray(row.statusHistory) ? row.statusHistory : [];
        const getStatusDate = status => {
            const matchingEntries = statusHistory.filter(entry =>
                String(entry?.status || "").toUpperCase() === status
            );
            const latestEntry = matchingEntries[matchingEntries.length - 1];
            return fmtTs(latestEntry?.changedAt) || "—";
        };
        const payDate = fmtTs(row.createdAt) || "—";
        const settledDate = getStatusDate("SETTLED");

        const status = row.status || "";
        const statusInfo = getStatusDisplayInfo(status);

        return (
            <TableRow key={row.transactionId || row.id || idx} hover>
                <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>{displayName}</Typography>
                    {row.memberNumber && (
                        <Typography variant="caption" style={{ color: "#888" }}>
                            Member: {row.memberNumber}
                        </Typography>
                    )}
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{payDate}</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{settledDate}</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{amountDisplay}</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{fundName}</Typography>
                    {usi && (
                        <Typography variant="caption" style={{ color: "#888" }}>USI: {usi}</Typography>
                    )}
                </TableCell>
                <TableCell>
                    {status ? (
                        <Chip
                            label={statusInfo.label}
                            style={{
                                backgroundColor: statusInfo.bg,
                                color: statusInfo.color,
                                fontWeight: 700,
                                fontSize: 12,
                                height: 26
                            }}
                        />
                    ) : (
                        <Typography variant="body2">—</Typography>
                    )}
                </TableCell>
            </TableRow>
        );
    };

    renderPagination = () => {
        const { pagination, currentPage, historyLoading } = this.state;
        if (!pagination) return null;

        const { total, totalPages, hasNext, hasPrevious } = pagination;
        const from = ((currentPage - 1) * PAGE_LIMIT) + 1;
        const to = Math.min(currentPage * PAGE_LIMIT, total);

        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", flexWrap: "wrap", gap: 8 }}>
                <Typography variant="body2" style={{ color: "#666" }}>
                    Showing {from}–{to} of {total} record{total !== 1 ? "s" : ""}
                </Typography>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!hasPrevious || historyLoading}
                        onClick={() => this.handlePageChange(currentPage - 1)}
                    >
                        Previous
                    </Button>
                    <Typography variant="body2" style={{ padding: "0 8px", color: "#333" }}>
                        Page {currentPage} of {totalPages}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!hasNext || historyLoading}
                        onClick={() => this.handlePageChange(currentPage + 1)}
                    >
                        Next
                    </Button>
                </div>
            </div>
        );
    };

    render() {
        const {
            employees, historyStartDate, historyEndDate, historyEmployeeId,
            contributionHistory, historyLoading, historyError, historySearched
        } = this.state;

        return (
            <div style={{ padding: 24 }}>
                <Grid container spacing={16} alignItems="flex-end" style={{ marginBottom: 24 }}>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={historyStartDate}
                            onChange={e => this.setState({ historyStartDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ placeholder: "dd/mm/yyyy" }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField
                            label="End Date"
                            type="date"
                            value={historyEndDate}
                            onChange={e => this.setState({ historyEndDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ placeholder: "dd/mm/yyyy" }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <FormControl fullWidth>
                            <InputLabel shrink>Payee</InputLabel>
                            <Select
                                value={historyEmployeeId}
                                onChange={e => this.setState({ historyEmployeeId: e.target.value })}
                                displayEmpty
                            >
                                <MenuItem value="">All Payees</MenuItem>
                                {employees.filter(emp => emp.isMapped === true || !!(emp.mygigstersEmployeeId || emp.employeeId)).map(emp => {
                                    const displayName = emp.profileName || `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || "—";
                                    return (
                                        <MenuItem key={emp.contactId} value={emp.contactId}>
                                            {displayName}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <Button
                            variant="contained"
                            disabled={historyLoading}
                            onClick={this.handleSearchHistory}
                            style={{ backgroundColor: HOT_PINK, color: "#fff", fontWeight: 700 }}
                            fullWidth
                        >
                            {historyLoading ? "Searching…" : "Search"}
                        </Button>
                    </Grid>
                </Grid>

                {historyLoading && (
                    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                        <CircularProgress style={{ color: HOT_PINK }} />
                    </div>
                )}

                {historyError && !historyLoading && (
                    <div style={{
                        backgroundColor: "#fce4ec",
                        border: "1px solid #e57373",
                        borderRadius: 6,
                        padding: "12px 16px"
                    }}>
                        <Typography variant="body2" style={{ color: "#c62828" }}>{historyError}</Typography>
                    </div>
                )}

                {!historyLoading && !historyError && historySearched && contributionHistory.length === 0 && (
                    <Typography variant="body2" style={{ color: "#aaa", textAlign: "center", padding: 32 }}>
                        No contribution history found for the selected filters.
                    </Typography>
                )}

                {!historyLoading && !historyError && contributionHistory.length > 0 && (
                    <div>
                        <Table>
                            <TableHead>
                                <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                                    <TableCell style={{ fontWeight: 700 }}>Employee</TableCell>
                                    <TableCell style={{ fontWeight: 700 }}>Pay Date</TableCell>
                                    <TableCell style={{ fontWeight: 700 }}>Settled Date</TableCell>
                                    <TableCell style={{ fontWeight: 700 }}>Amount</TableCell>
                                    <TableCell style={{ fontWeight: 700 }}>Fund</TableCell>
                                    <TableCell style={{ fontWeight: 700 }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contributionHistory.map((row, idx) => this.renderRow(row, idx))}
                            </TableBody>
                        </Table>
                        {this.renderPagination()}
                    </div>
                )}
            </div>
        );
    }
}

export default HGSuperPayContributionHistory;
